import React, { useEffect } from 'react';
import {
    Mic, Network, Calendar, Infinity as InfinityIcon,
    Target, Layers3, Repeat, Radio, ArrowRight,
    Compass, Landmark, Hourglass,
    Building2, Newspaper, GraduationCap, PenTool, Trees,
    TrendingUp, Users, Coins,
    Hammer, Volume2, Flame, LineChart,
    Film, Image as ImageIcon, MessageCircle, Sparkles,
    Clock, Rocket, ShieldCheck,
    Video, FolderOpen, Camera, MapPin, Wrench, Scale,
    Workflow, BadgeCheck, Layers,
} from 'lucide-react';
import { GlassCard } from './reports/_shared/GlassCard';

const SectionLabel = ({ children }) => (
    <p className="text-xs uppercase tracking-[0.3em] text-emerald-300/70 font-mono mb-4">{children}</p>
);

const Pill = ({ icon: Ico, children, tone = "emerald" }) => {
    const map = {
        emerald: "bg-emerald-500/10 border-emerald-500/20 text-emerald-200",
        gold: "bg-amber-500/10 border-amber-500/30 text-amber-200",
        muted: "bg-white/5 border-white/10 text-white/60",
    };
    return (
        <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium tracking-wide border ${map[tone]}`}>
            {Ico && <Ico className="w-3.5 h-3.5" />}
            {children}
        </span>
    );
};

const realityCards = [
    { icon: Target, title: "Cold pipelines miss the buyer", body: "Cr+ buyers don't act on ads. They act on names they trust." },
    { icon: Layers3, title: "Every plotted-dev reads the same", body: "Sectors, acres, amenities. The asset stopped being the differentiator." },
    { icon: Repeat, title: "Referrals don't compound", body: "Your best leads are passive. We turn them into infrastructure." },
];

const wedgePoints = [
    { icon: Radio, title: "Distribution before lead-gen", body: "Buyers route to you. Not the other way." },
    { icon: Landmark, title: "Owned, not borrowed", body: "Algorithms change. A network you built compounds for years." },
    { icon: Hourglass, title: "Compound, don't campaign", body: "Months 1–3 build. Months 4–12 return." },
];

const phase1Bullets = [
    { icon: Mic, text: "2 episodes / month · 60–90 min long-form" },
    { icon: Users, text: "Founder solo + curated guests (industry, journalists, architects)" },
    { icon: Film, text: "Each ep → 12–16 vertical clips for the network" },
];

const phase2Pages = [
    { icon: BadgeCheck, name: "Founder-led page", sub: "the recognised voice" },
    { icon: Newspaper, name: "Sonipat / NCR market commentary", sub: "category authority" },
    { icon: GraduationCap, name: "Plotted-dev / land investing", sub: "education layer" },
    { icon: PenTool, name: "Architecture + design IP", sub: "craft + aesthetic" },
    { icon: Trees, name: "Modern Prastha · lifestyle", sub: "community + culture" },
];

const proofCards = [
    { icon: Flame, headline: "32M views", sub: "250 posts · 5 pages · 2 months", body: "Apache / Siddhart fan-page network. The exact playbook.", tone: "emerald" },
    { icon: TrendingUp, headline: "0 → 10K", sub: "4.29M views · ₹1M pipeline · 90 days", body: "Kush Desai. Non-creator founder. No face on camera.", tone: "emerald" },
    { icon: Coins, headline: "₹12L at 2K", sub: "98% below avg influencer count", body: "Distribution beats follower count for high-ticket.", tone: "gold" },
];

const phases = [
    {
        icon: Hammer,
        label: "Phase 0", weeks: "Wk 1–2", title: "Foundation",
        bullets: ["Founder voice + positioning doc", "Podcast architecture + recording setup", "Page network architecture · 5 pages", "Visual system + 6-piece moodboard"],
        kpi: null,
    },
    {
        icon: Volume2,
        label: "Phase 1", weeks: "Wk 3–6", title: "Voice lock",
        bullets: ["First 2 podcast eps shipped", "2 fan pages live · 8–10 pcs/wk each", "Founder carousels: 1–2 / week", "Pure organic — read what hooks"],
        kpi: { a: "Voice locked", b: "1 clip > 100K views", c: "Founder feed: 1K+" },
    },
    {
        icon: Flame,
        label: "Phase 2", weeks: "Wk 7–12", title: "Traction",
        bullets: ["All 5 pages live · 4–5 pcs/wk each", "6 podcast eps cumulative", "Steady-state: 140–180 pcs/mo", "1–2 paid pulses on top organic (₹40K–₹80K)"],
        kpi: { a: "5M+ views in M3", b: "1+ piece > 1M", c: "10M+ network reach" },
    },
    {
        icon: LineChart,
        label: "Phase 3", weeks: "M 4–12", title: "Compound",
        bullets: ["Network self-compounds · less new, more remix", "Founder = recognised category voice", "Inbound bypasses broker layer", "Optional: extend to 2nd founder voice"],
        kpi: null,
    },
];

const productionTiles = [
    { icon: Mic, vol: "2", label: "Podcast eps", sub: "60–90 min long-form" },
    { icon: Film, vol: "24–32", label: "Podcast clips", sub: "vertical short-form" },
    { icon: Network, vol: "80–120", label: "Fan-page posts", sub: "across 5 pages" },
    { icon: ImageIcon, vol: "4–6", label: "Founder carousels", sub: "editorial pull-quotes" },
    { icon: MessageCircle, vol: "30+", label: "Stories", sub: "BTS · polls · Q&As" },
    { icon: Sparkles, vol: "140–180", label: "Total / month", sub: "after 90-day ramp", highlight: true },
];

const longGameTiles = [
    { icon: Hammer, period: "Months 1–3", title: "Build the engine", body: "Voice, podcast, page network." },
    { icon: Rocket, period: "Months 4–12", title: "Engine compounds", body: "You stop paying for reach. It returns anyway." },
    { icon: ShieldCheck, period: "The point", title: "Match the buyer's rhythm", body: "Plotted-dev cycles run in months. So does this." },
];

const openQuestions = [
    { icon: Video, q: "Founder availability for podcast (2–3 hrs / mo)?" },
    { icon: FolderOpen, q: "Existing content we can mine for source IP?" },
    { icon: Building2, q: "Project pipeline beyond Elite Residences?" },
    { icon: Camera, q: "Drone footage / project photography ready?" },
    { icon: MapPin, q: "Sonipat-only or NCR-wide voice?" },
    { icon: Wrench, q: "In-house marketing person or full agency-of-record?" },
    { icon: Scale, q: "RERA / pricing-disclosure constraints?" },
    { icon: Calendar, q: "Retainer length + payment cadence?" },
];

const whyCards = [
    { icon: Workflow, title: "Stack already running", body: "Day-one capacity. We ship, not hire." },
    { icon: BadgeCheck, title: "Proven category-voice playbook", body: "32M views. ₹1M pipeline. Cold starts solved." },
    { icon: Layers, title: "Single point of accountability", body: "Strategy → posting → reporting. One team, one number." },
];

export default function OnePrasthaProposal() {
    useEffect(() => {
        document.title = "SocialSEO × One Prastha — Founder-Led Brand Proposal";
    }, []);

    return (
        <div className="min-h-screen bg-transparent font-sans leading-relaxed text-white overflow-x-hidden selection:bg-emerald-500/30 pb-32">
            <div className="fixed inset-0 pointer-events-none z-0" style={{
                background: `radial-gradient(circle at 80% 10%, rgba(16, 185, 129, 0.18), transparent 55%),
                             radial-gradient(circle at 15% 85%, rgba(217, 119, 6, 0.18), transparent 60%)`
            }}></div>

            <div className="relative z-10">
                <header className="fixed top-0 left-0 right-0 z-50 p-6 flex justify-between items-center bg-transparent pointer-events-none">
                    <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-4 py-2 rounded-full pointer-events-auto">
                        <span className="font-bold tracking-tight text-white">Social<span className="text-emerald-400">SEO</span></span>
                    </div>
                    <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-4 py-2 rounded-full pointer-events-auto">
                        <span className="text-xs text-white/70 font-mono tracking-widest uppercase">One Prastha · Founder-Led Brand</span>
                    </div>
                </header>

                <main className="container mx-auto px-4 pt-32 space-y-24">

                    {/* Hero — title only, no paragraph block */}
                    <section className="text-center max-w-5xl mx-auto">
                        <SectionLabel>Founder-Led Brand · 12-Month Engine</SectionLabel>
                        <h1 className="text-5xl md:text-7xl font-black tracking-tight mb-6 leading-[1.05]">
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-emerald-100 to-emerald-300">
                                Real estate doesn&apos;t move on ads.
                            </span>
                            <br />
                            <span className="text-white/70 font-light italic">It moves on memory.</span>
                        </h1>
                        <p className="text-lg md:text-xl text-emerald-200/90 mb-10 max-w-2xl mx-auto">
                            We build the distribution that makes your name unforgettable — before the buyer ever picks up the phone.
                        </p>
                        <div className="flex flex-wrap items-center justify-center gap-3">
                            <Pill icon={Mic} tone="emerald">Founder podcast</Pill>
                            <Pill icon={Network} tone="emerald">5-page network</Pill>
                            <Pill icon={Calendar} tone="gold">90-day kickoff</Pill>
                            <Pill icon={InfinityIcon} tone="muted">12-month engine</Pill>
                        </div>
                    </section>

                    {/* 1 · The Reality — icon + headline + 1 line */}
                    <section className="max-w-6xl mx-auto">
                        <SectionLabel>1 · The Reality</SectionLabel>
                        <h2 className="text-2xl md:text-4xl font-bold mb-10 max-w-3xl">
                            Performance marketing has a price ceiling. <span className="text-emerald-300">You sit above it.</span>
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            {realityCards.map((c, i) => (
                                <GlassCard key={i} className="border-t-4 border-t-emerald-500/60">
                                    <c.icon className="w-8 h-8 text-emerald-300 mb-4" />
                                    <p className="font-bold text-white mb-2">{c.title}</p>
                                    <p className="text-white/65 text-sm leading-relaxed">{c.body}</p>
                                </GlassCard>
                            ))}
                        </div>
                    </section>

                    {/* 2 · The Wedge — positioning quote + 3 icon cells */}
                    <section className="max-w-5xl mx-auto">
                        <SectionLabel>2 · The Wedge</SectionLabel>
                        <GlassCard className="text-center py-12 border border-emerald-500/30">
                            <Compass className="w-10 h-10 text-emerald-300 mx-auto mb-4" />
                            <p className="text-2xl md:text-4xl font-bold leading-tight max-w-3xl mx-auto">
                                <span className="text-white/90">You don&apos;t sell plots.</span>{" "}
                                <span className="bg-clip-text text-transparent bg-gradient-to-r from-emerald-200 to-amber-300">You build legacies.</span>
                            </p>
                            <p className="text-white/50 text-sm mt-4 max-w-xl mx-auto">Then make the founder behind the legacy the one buyers already trust.</p>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10 text-left">
                                {wedgePoints.map((w, i) => (
                                    <div key={i} className="flex items-start gap-3">
                                        <w.icon className="w-6 h-6 text-emerald-300 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-emerald-200 font-bold text-sm">{w.title}</p>
                                            <p className="text-white/55 text-xs leading-relaxed mt-1">{w.body}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </GlassCard>
                    </section>

                    {/* 3 · Two-Phase Build — icon header + tight bullets */}
                    <section className="max-w-7xl mx-auto">
                        <SectionLabel>3 · The Two-Phase Build</SectionLabel>
                        <h2 className="text-2xl md:text-4xl font-bold mb-10 max-w-3xl">
                            One engine. <span className="text-emerald-300">Two halves.</span>
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <GlassCard className="border-l-4 border-l-emerald-500/60">
                                <div className="flex items-start gap-4 mb-5">
                                    <Mic className="w-10 h-10 text-emerald-300 flex-shrink-0" />
                                    <div>
                                        <p className="text-emerald-300/70 font-mono text-xs tracking-widest">PHASE 1</p>
                                        <h3 className="text-2xl font-bold mt-1">Podcast as positioning engine</h3>
                                    </div>
                                </div>
                                <ul className="space-y-3">
                                    {phase1Bullets.map((b, i) => (
                                        <li key={i} className="flex items-center gap-3 text-white/80 text-sm">
                                            <b.icon className="w-4 h-4 text-emerald-300 flex-shrink-0" />
                                            <span>{b.text}</span>
                                        </li>
                                    ))}
                                </ul>
                            </GlassCard>

                            <GlassCard className="border-l-4 border-l-amber-500/60">
                                <div className="flex items-start gap-4 mb-3">
                                    <Network className="w-10 h-10 text-amber-300 flex-shrink-0" />
                                    <div>
                                        <p className="text-amber-300/70 font-mono text-xs tracking-widest">PHASE 2</p>
                                        <h3 className="text-2xl font-bold mt-1">Fan-page distribution network</h3>
                                    </div>
                                </div>
                                <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 mb-4 flex items-center gap-3">
                                    <Flame className="w-5 h-5 text-amber-300 flex-shrink-0" />
                                    <p className="text-amber-200 text-sm font-semibold">32M views · 250 posts · 5 pages · 2 months</p>
                                </div>
                                <ul className="space-y-2.5">
                                    {phase2Pages.map((p, i) => (
                                        <li key={i} className="flex items-center gap-3 text-white/80 text-sm">
                                            <p.icon className="w-4 h-4 text-amber-300 flex-shrink-0" />
                                            <span><span className="font-semibold">{p.name}</span> <span className="text-white/45">— {p.sub}</span></span>
                                        </li>
                                    ))}
                                </ul>
                            </GlassCard>
                        </div>
                    </section>

                    {/* 4 · Proof — big numbers, 1-line bodies */}
                    <section className="max-w-7xl mx-auto">
                        <SectionLabel>4 · Proof</SectionLabel>
                        <h2 className="text-2xl md:text-4xl font-bold mb-10 max-w-3xl">
                            We&apos;ve built this engine before. <span className="text-emerald-300">Three different ways.</span>
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            {proofCards.map((p, i) => (
                                <GlassCard key={i} className={`border-t-2 ${p.tone === 'gold' ? 'border-t-amber-500/60' : 'border-t-emerald-500/60'}`}>
                                    <p.icon className={`w-7 h-7 mb-3 ${p.tone === 'gold' ? 'text-amber-300' : 'text-emerald-300'}`} />
                                    <p className={`text-3xl md:text-4xl font-black mb-1 ${p.tone === 'gold' ? 'text-amber-200' : 'text-emerald-200'}`}>{p.headline}</p>
                                    <p className="text-white/55 text-xs font-mono tracking-widest uppercase mb-3">{p.sub}</p>
                                    <p className="text-white/75 text-sm leading-relaxed">{p.body}</p>
                                </GlassCard>
                            ))}
                        </div>
                    </section>

                    {/* 5 · 90-Day Rollout — phase cards with icons, tighter bullets */}
                    <section className="max-w-7xl mx-auto">
                        <SectionLabel>5 · 90-Day Rollout</SectionLabel>
                        <h2 className="text-2xl md:text-4xl font-bold mb-10 max-w-3xl">
                            Foundation → voice lock → traction → <span className="text-emerald-300">compound.</span>
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {phases.map((ph, i) => (
                                <GlassCard key={i} className={`${i === phases.length - 1 ? 'border border-amber-500/30' : 'border border-emerald-500/20'}`}>
                                    <div className="flex items-start gap-4 mb-4">
                                        <ph.icon className={`w-9 h-9 flex-shrink-0 ${i === phases.length - 1 ? 'text-amber-300' : 'text-emerald-300'}`} />
                                        <div>
                                            <p className={`font-mono text-xs tracking-widest ${i === phases.length - 1 ? 'text-amber-300/70' : 'text-emerald-300/70'}`}>{ph.label} · {ph.weeks}</p>
                                            <h3 className="text-xl font-bold">{ph.title}</h3>
                                        </div>
                                    </div>
                                    <ul className="space-y-2 mb-3">
                                        {ph.bullets.map((b, j) => (
                                            <li key={j} className="flex items-start gap-2 text-white/75 text-sm leading-relaxed">
                                                <span className={`mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 ${i === phases.length - 1 ? 'bg-amber-400' : 'bg-emerald-400'}`}></span>
                                                <span>{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    {ph.kpi && (
                                        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-3 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                                            <span className="text-emerald-300/70 font-mono uppercase tracking-widest">KPI</span>
                                            <span className="text-white/85">{ph.kpi.a}</span>
                                            <span className="text-white/55">·</span>
                                            <span className="text-white/85">{ph.kpi.b}</span>
                                            <span className="text-white/55">·</span>
                                            <span className="text-white/85">{ph.kpi.c}</span>
                                        </div>
                                    )}
                                </GlassCard>
                            ))}
                        </div>
                    </section>

                    {/* 6 · Production Engine — stat tiles, no table */}
                    <section className="max-w-6xl mx-auto">
                        <SectionLabel>6 · Production Engine</SectionLabel>
                        <h2 className="text-2xl md:text-4xl font-bold mb-10 max-w-3xl">
                            Same stack we already run for <span className="text-emerald-300">Kush, Sachin, Source Lab.</span>
                        </h2>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            {productionTiles.map((t, i) => (
                                <GlassCard key={i} className={`text-center ${t.highlight ? 'border-2 border-emerald-500/40 bg-emerald-500/5' : ''}`}>
                                    <t.icon className={`w-7 h-7 mx-auto mb-3 ${t.highlight ? 'text-emerald-200' : 'text-emerald-300'}`} />
                                    <p className={`text-3xl md:text-4xl font-black ${t.highlight ? 'text-emerald-200' : 'text-white'}`}>{t.vol}</p>
                                    <p className="text-white/85 font-semibold text-sm mt-1">{t.label}</p>
                                    <p className="text-white/45 text-xs mt-1">{t.sub}</p>
                                </GlassCard>
                            ))}
                        </div>
                    </section>

                    {/* 7 · Long Game — 3 icon stats, no paragraphs */}
                    <section className="max-w-6xl mx-auto">
                        <SectionLabel>7 · Why this is a long game</SectionLabel>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            {longGameTiles.map((t, i) => (
                                <GlassCard key={i} className="border-t-2 border-t-emerald-500/60">
                                    <t.icon className="w-9 h-9 text-emerald-300 mb-4" />
                                    <p className="text-emerald-300/70 font-mono text-xs tracking-widest uppercase mb-2">{t.period}</p>
                                    <p className="text-xl font-bold text-white mb-2">{t.title}</p>
                                    <p className="text-white/65 text-sm leading-relaxed">{t.body}</p>
                                </GlassCard>
                            ))}
                        </div>
                        <p className="text-center text-emerald-200/70 text-sm italic mt-6 max-w-xl mx-auto">
                            A 30-day result in this category means someone is solving the wrong problem.
                        </p>
                    </section>

                    {/* 8 · Open Questions — icon grid, 2 cols x 4 rows */}
                    <section className="max-w-6xl mx-auto">
                        <SectionLabel>8 · Lock at kickoff</SectionLabel>
                        <h2 className="text-2xl md:text-4xl font-bold mb-10 max-w-3xl">
                            Eight questions. <span className="text-emerald-300">15 minutes.</span>
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {openQuestions.map((q, i) => (
                                <div key={i} className="flex items-center gap-3 p-4 rounded-xl border border-white/10 bg-white/5 hover:bg-emerald-500/5 hover:border-emerald-500/30 transition-colors">
                                    <span className="text-emerald-300 font-mono text-xs font-bold flex-shrink-0">{String(i + 1).padStart(2, '0')}</span>
                                    <q.icon className="w-5 h-5 text-emerald-300/80 flex-shrink-0" />
                                    <span className="text-white/85 text-sm">{q.q}</span>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* 9 · Why SocialSEO + CTA */}
                    <section className="max-w-5xl mx-auto">
                        <SectionLabel>9 · Why SocialSEO</SectionLabel>
                        <GlassCard className="text-center py-12 border border-emerald-500/30">
                            <h2 className="text-2xl md:text-4xl font-bold mb-10 max-w-3xl mx-auto leading-tight">
                                One team. <span className="text-emerald-300">No handoff seams.</span>
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 text-left mb-10 max-w-4xl mx-auto">
                                {whyCards.map((w, i) => (
                                    <div key={i}>
                                        <w.icon className="w-8 h-8 text-emerald-300 mb-3" />
                                        <p className="text-white/90 font-semibold text-sm mb-1">{w.title}</p>
                                        <p className="text-white/55 text-xs leading-relaxed">{w.body}</p>
                                    </div>
                                ))}
                            </div>
                            <a
                                href="https://calendly.com/suresh-socialseo/socialseo-instant-report-clone-1"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-emerald-500 to-amber-500 text-white font-bold rounded-full text-lg hover:scale-105 hover:shadow-2xl hover:shadow-emerald-500/30 transition-all duration-300"
                            >
                                <Clock className="w-5 h-5" />
                                Book a kickoff call
                                <ArrowRight className="w-5 h-5" />
                            </a>
                            <p className="text-white/40 text-xs mt-4 font-mono tracking-widest uppercase">15 min · 8 questions · no commitment</p>
                        </GlassCard>
                    </section>

                    {/* Footer */}
                    <footer className="max-w-5xl mx-auto text-center pt-8 border-t border-white/5">
                        <p className="text-white/40 text-sm">
                            SocialSEO LLP · Bangalore · 2026 · Prepared for <span className="text-emerald-300">One Prastha Realty</span> · @oneprastha
                        </p>
                    </footer>
                </main>
            </div>
        </div>
    );
}
